import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import { useEventListener, useMediaQuery } from 'usehooks-ts'
import { ComplementaryProductsContainer } from '@components/ComplementaryProductsContainer/ComplementaryProductsContainer'
import { ProductsSlider } from '@components/ProductsSlider/ProductsSlider'
import { dataTestIds } from '@velasca/constants'
import { ResponsiveImage } from '@velasca/ui/responsive-image'
import { Video } from '@velasca/ui/video'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { buildWidthSources } from '@velasca/utils/build-width-sources'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { trackEvent } from '@velasca/utils/track-event'
import * as S from './ComplementaryProducts.styles'
import type { ComplementaryProductsProps as ComplementaryProductsProps } from './ComplementaryProducts.types'

const quickAddEvent = (handle: string) => {
  trackEvent({
    googleAnalytics: {
      eventName: 'complementary_quick_add',
      data: { handle },
    },
  })
}

const addToCartEvent = (handle: string) => {
  trackEvent({
    googleAnalytics: {
      eventName: 'complementary_quick_add_to_cart',
      data: { handle },
    },
  })
}

export const ComplementaryProducts = forwardRef<
  HTMLDivElement,
  ComplementaryProductsProps
>(
  (
    {
      className,
      sliderStyles,
      products,
      loading = false,
      parentLoading = false,
      arrowVisible = false,
      breakpoints,
      editorialImage,
      editorialVideo,
      onQuickAdd,
      onAddToCart,
      layout = 'standard',
      loadingSlides,
      title,
      subTitle,
      shouldOpenCartDrawerOnAddToCart,
    },
    ref,
  ) => {
    const editorialImageRef = useRef<HTMLImageElement>(null)
    const productListRef = useRef<HTMLDivElement>(null)

    const isDesktopViewport = useMediaQuery('(min-width: 1200px)')
    const { t } = useTranslation('product')

    const isRedesignMode = useMemo(
      () => isDesktopViewport && (editorialImage || editorialVideo),
      [isDesktopViewport, editorialImage, editorialVideo],
    )

    /**
     * This is necessary in "Buy the Look" sections at the bottom of some lookbook pages.
     */
    const matchHeights = useCallback(() => {
      if (!editorialImageRef.current || !productListRef.current) {
        return
      }

      const editorialImageRect =
        editorialImageRef.current.getBoundingClientRect()

      productListRef.current.style.height = `${editorialImageRect.height}px`
    }, [])

    useEventListener('resize', matchHeights)

    useEffect(() => {
      matchHeights()
    }, [matchHeights])

    const handleQuickAdd = useCallback(
      (handle: string | undefined) => {
        if (handle) {
          onQuickAdd ? onQuickAdd(handle) : quickAddEvent(handle)
        }
      },
      [onQuickAdd],
    )

    const handleAddToCart = useCallback(
      (handle: string | undefined) => {
        if (handle) {
          onAddToCart ? onAddToCart(handle) : addToCartEvent(handle)
        }
      },
      [onAddToCart],
    )

    const slider = useMemo(() => {
      return (
        <ProductsSlider
          showProductType
          shouldOpenCartDrawerOnAddToCart={shouldOpenCartDrawerOnAddToCart}
          className={`product-slider ${className}`}
          sliderStyles={sliderStyles}
          loadingSlides={loadingSlides}
          products={products}
          loading={loading || parentLoading}
          arrowVisible={arrowVisible}
          breakpoints={breakpoints}
          dataTestId={dataTestIds.COMPLEMENTARY_PRODUCTS_SLIDER}
          title={title ?? t('crossProductsTitle')}
          subTitle={subTitle}
          onQuickAdd={handleQuickAdd}
          onAddToCart={handleAddToCart}
        />
      )
    }, [
      shouldOpenCartDrawerOnAddToCart,
      className,
      sliderStyles,
      loadingSlides,
      products,
      loading,
      parentLoading,
      arrowVisible,
      breakpoints,
      title,
      t,
      subTitle,
      handleQuickAdd,
      handleAddToCart,
    ])

    const list = useMemo(() => {
      return (
        <ComplementaryProductsContainer
          showProductType
          loading={loading || parentLoading}
          dataTestId={dataTestIds.COMPLEMENTARY_PRODUCTS_SLIDER}
          ref={productListRef}
          products={products}
          layout={layout}
          onQuickAdd={handleQuickAdd}
          onAddToCart={handleAddToCart}
        />
      )
    }, [
      loading,
      parentLoading,
      products,
      layout,
      handleQuickAdd,
      handleAddToCart,
    ])

    if ((!products || isEmpty(products)) && !loading) {
      return null
    }

    // Prepare image configurations outside JSX
    // Approximate mobile CSS width of the image (observed ~390px)
    const mobileCssWidth = 390
    const desktopImageWidth = Math.floor(1290 * 0.5)

    // Mobile image configuration
    const mobileImageConfig = editorialImage
      ? buildWidthSources(
          editorialImage,
          [{ maxWidth: 767, imageWidth: mobileCssWidth }],
          mobileCssWidth * 2,
        )
      : null

    // Desktop image configuration
    const desktopImageConfig = editorialImage
      ? buildWidthSources(
          editorialImage,
          [{ maxWidth: 1200, imageWidth: desktopImageWidth }],
          desktopImageWidth,
        )
      : null

    // Mobile fallback src: a 1x width to avoid oversized downloads while srcset selects sharper variants
    const mobileFallbackSrc = editorialImage
      ? setQueryParams(editorialImage, [
          { name: 'width', value: mobileCssWidth.toString() },
        ])
      : ''

    // const desktopLqipSrc = editorialImage
    //   ? setQueryParams(editorialImage, [{ name: 'width', value: '30' }])
    //   : ''

    return (
      <S.Container ref={ref} layout={layout}>
        {!isDesktopViewport && editorialImage && mobileImageConfig && (
          <S.ImageContainer>
            <ResponsiveImage
              lazyload
              lqip
              ref={editorialImageRef}
              imgSrc={mobileFallbackSrc}
              sources={mobileImageConfig.sources}
              sizes={mobileImageConfig.sizes}
            />
          </S.ImageContainer>
        )}
        {isRedesignMode && (editorialImage || editorialVideo) ? (
          <>
            <S.Title>{title ?? t('crossProductsTitle')}</S.Title>
            <S.Content layout={layout}>
              <S.ImageContainer>
                {editorialImage && !editorialVideo && (
                  <ResponsiveImage
                    lazyload
                    lqip
                    ref={editorialImageRef}
                    sizes={desktopImageConfig?.sizes}
                    sources={buildIntrinsicWidthSources({
                      src: editorialImage,
                    })}
                    imgSrc={setQueryParams(editorialImage, [
                      { name: 'width', value: '30' },
                    ])}
                    onLoad={matchHeights}
                  />
                )}
                {editorialVideo && !editorialImage && (
                  <Video
                    poster={editorialVideo.poster}
                    sources={editorialVideo.sources}
                  />
                )}
              </S.ImageContainer>
              {list}
            </S.Content>
          </>
        ) : (
          slider
        )}
      </S.Container>
    )
  },
)

ComplementaryProducts.displayName = 'ComplementaryProducts'
